import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { MeilleursBiensService } from 'app/services/meilleuresBiens/meilleurs-biens.service';
import { SousBienService } from 'app/services/sousBiens/sous-bien.service';
import { BiensImmobsBiensImmobs } from 'app/services/biens-immobs/biens-immobs.service'; // Import the service
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';

interface SousBien {
  id: number;
  name: string;
  [key: string]: any;
}

interface Bien {
  id: number;
  name: string;
  agencyName?: string;
  imageUrl?: string;
  status?: string;
  createdAt?: string;
  displayOrder?: number;
  orderIndex?: number;
  sous_biens?: SousBien[];
}

@Component({
  selector: 'app-list-bien',
  templateUrl: './list-bien.component.html',
  styleUrls: ['./list-bien.component.scss']
})
export class ListBienComponent implements OnInit {
  idAgency: any;
  Agency: any;
  list: Bien[] = [];
  dataSource = new MatTableDataSource();
  idParams: string;
  isLoading = false;

  constructor(
    private agencieService: AgenciesService,
    private activatedRoute: ActivatedRoute,
    private meilleureService: MeilleursBiensService,
    private sousBienService: SousBienService,
    private biensImmobsService: BiensImmobsBiensImmobs, // Inject the service
    private location: Location,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.idParams = this.activatedRoute.snapshot.paramMap.get('id');
    console.log('ID param reçu :', this.idParams);

    this.idAgency = this.idParams;
    this.agencieService.setIdAgency(this.idAgency);

    this.loadAgencyDetails();
  }

  loadAgencyDetails(): void {
    this.isLoading = true;
    this.agencieService.getAgencieById(this.idAgency).subscribe({
      next: (data: any) => {
        this.Agency = data;
        const biens = Array.isArray(data?.biens) ? data.biens : [];
        this.list = biens.map((b: Bien) => ({ ...b, sous_biens: [] }));

        // Initialize order indexes and sort by displayOrder if present
        this.initializeOrderIndexes();

        console.log('Biens chargés depuis l’agence :', this.list);

        if (this.list.length > 0) {
          this.loadSousBiensForBiens();
        } else {
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error(err);
        this.isLoading = false;
        Swal.fire({
          title: 'Erreur',
          text: 'Impossible de charger les biens de l’agence.',
          icon: 'error'
        });
      }
    });
  }

  private initializeOrderIndexes(): void {
    const hasOrder = this.list.some(b => b.displayOrder !== undefined && b.displayOrder !== null);
    if (hasOrder) {
      // Sort by displayOrder ascending
      this.list.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    } else {
      // Fallback to createdAt (oldest first)
      this.list.sort((a, b) => {
        const dateA = this.parseDate(a.createdAt);
        const dateB = this.parseDate(b.createdAt);
        return dateA.getTime() - dateB.getTime();
      });
    }
    // Assign sequential orderIndex for UI
    this.list.forEach((item, idx) => {
      item.orderIndex = idx + 1;
      item.displayOrder = idx;
    });
  }

  private loadSousBiensForBiens(): void {
    const requests = this.list.map(bien => this.sousBienService.getBiensById(bien.id));
    forkJoin(requests).subscribe({
      next: (responses: any[]) => {
        responses.forEach((fullBien, index) => {
          this.list[index].sous_biens = fullBien?.sous_biens || [];
        });
        console.log('Sous‑biens assignés :', this.list.map(b => ({ id: b.id, count: b.sous_biens?.length })));
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Erreur lors du chargement des sous‑biens individuels :', err);
        this.isLoading = false;
        Swal.fire({
          title: 'Attention',
          text: 'Impossible de charger les sous‑biens.',
          icon: 'warning',
          timer: 2000,
          showConfirmButton: false
        });
      }
    });
  }

  private parseDate(dateString: string): Date {
    if (!dateString) return new Date(0);
    const parts = dateString.split(/[\s/:]+/);
    if (parts.length >= 6) {
      const [day, month, year, hour, minute, second] = parts.map(Number);
      return new Date(year, month - 1, day, hour, minute, second);
    }
    return new Date(dateString);
  }

  // ============================================================
  // ORDERING METHODS (Batch update)
  // ============================================================

  private saveAllOrdersBatch(): void {
    if (this.list.length === 0) return;
    this.isLoading = true;
    const updates = this.list.map(bien => ({
      id: bien.id,
      displayOrder: bien.displayOrder!
    }));
    this.biensImmobsService.updateBiensOrderBatch(updates).subscribe({
      next: () => {
        this.isLoading = false;
        Swal.fire({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 1500,
          icon: 'success',
          title: 'Ordre mis à jour'
        });
      },
      error: (err) => {
        console.error('Batch update error:', err);
        this.isLoading = false;
        Swal.fire({
          title: 'Erreur',
          text: 'Impossible de sauvegarder l\'ordre. Rechargement...',
          icon: 'error'
        });
        this.loadAgencyDetails(); // reload to revert
      }
    });
  }

  private updateOrdersAndSave(): void {
    this.list.forEach((bien, idx) => {
      bien.orderIndex = idx + 1;
      bien.displayOrder = idx;
    });
    this.saveAllOrdersBatch();
  }

  drop(event: CdkDragDrop<Bien[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.list, event.previousIndex, event.currentIndex);
    this.updateOrdersAndSave();
  }

  moveUp(index: number): void {
    if (index === 0) return;
    [this.list[index], this.list[index - 1]] = [this.list[index - 1], this.list[index]];
    this.updateOrdersAndSave();
  }

  moveDown(index: number): void {
    if (index === this.list.length - 1) return;
    [this.list[index], this.list[index + 1]] = [this.list[index + 1], this.list[index]];
    this.updateOrdersAndSave();
  }

  // ============================================================
  // DELETE & NAVIGATION
  // ============================================================

  getData(): void {
    this.loadAgencyDetails();
  }

  deleteAgenciesProjet(bien: Bien): void {
    Swal.fire({
      title: 'Êtes-vous sûr(e) ?',
      text: 'Vous ne pourrez pas revenir en arrière !',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, supprimez-le.'
    }).then((result) => {
      if (result.isConfirmed) {
        this.meilleureService.deleteMBiens(bien.id).subscribe({
          next: () => {
            Swal.fire('Supprimé !', 'Le bien a été supprimé.', 'success');
            this.list = this.list.filter(b => b.id !== bien.id);
            this.updateOrdersAndSave(); // re-index after deletion
          },
          error: (err) => {
            console.error(err);
            Swal.fire('Erreur', 'La suppression a échoué.', 'error');
          }
        });
      }
    });
  }

  navigateBack(): void {
    this.location.back();
  }

  storeItemId(item: { id: string }): void {
    localStorage.setItem('storedItemId', item.id);
  }

  sendIdToService(): void {
    this.agencieService.handleId(this.idParams);
  }
}